// COGS explorer — deterministic sample of per-call usage.
//
// Backs the "Load sample data" affordance so the measured/median flow is fully
// exercisable before the backend ships GET /adm/cogs_usage. Shape matches the
// CogsUsageRow contract exactly, so swapping in the real Sync response is a no-op.

import type { CogsUsageRow } from './compute';

// Small seeded PRNG (mulberry32) — deterministic so the sample never shifts.
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Shape {
  action: string;
  inMean: number;
  outMean: number;
  chars?: number;
  perUserMax: number; // max calls a user makes of this action in the window
}

const SHAPES: Shape[] = [
  { action: 'prospect_research', inMean: 6000, outMean: 1500, perUserMax: 6 },
  { action: 'generate_prospect_messages', inMean: 4000, outMean: 1200, perUserMax: 8 },
  { action: 'enrich_prospect', inMean: 0, outMean: 0, perUserMax: 10 },
  { action: 'fa_analysis', inMean: 3000, outMean: 1500, perUserMax: 2 },
  { action: 'coaching_turn', inMean: 8000, outMean: 800, chars: 800, perUserMax: 12 },
];

/** ~30 synthetic users, jittered per-call usage. Stable across reloads. */
export function sampleUsageRows(): CogsUsageRow[] {
  const rand = rng(1_729);
  const rows: CogsUsageRow[] = [];
  const users = 30;
  for (let u = 0; u < users; u++) {
    const userId = `sample-user-${String(u + 1).padStart(2, '0')}`;
    for (const shape of SHAPES) {
      const nCalls = Math.floor(rand() * (shape.perUserMax + 1));
      for (let c = 0; c < nCalls; c++) {
        const jitter = (base: number) => (base === 0 ? undefined : Math.round(base * (0.7 + rand() * 0.6)));
        rows.push({
          user_id: userId,
          action: shape.action,
          input_tokens: jitter(shape.inMean),
          output_tokens: jitter(shape.outMean),
          chars: shape.chars ? Math.round(shape.chars * (0.7 + rand() * 0.6)) : undefined,
          calls: 1,
        });
      }
    }
  }
  return rows;
}
